/**
 * Isolated external store for the LOCAL mic input level (0..1), driven by the voice-client VAD at
 * ~8 Hz while the player talks (perf-audit 2026-06-24, finding R1).
 *
 * WHY this exists: the level previously flowed voice-client → `setInputLevel` (OfficePage state) →
 * prop → the 3700-line `HUD`, which has no `React.memo` — so every ~120 ms of speech reconciled the
 * ENTIRE HUD subtree just to repaint one 1-line bar, stealing main-thread time from the Pixi render
 * loop (felt as micro-jank while talking, at any population). Routing the level through this tiny
 * pub/sub, consumed by EXACTLY one leaf (`LevelMeter`) via `useSyncExternalStore`, means the 8 Hz
 * updates re-render only that bar. (The Pixi speaking-ring already bypasses React the same way, via
 * engine refs.)
 *
 * Deliberately a bare pub/sub, not Zustand: one writer (the voice-client callback in OfficePage),
 * one reader (LevelMeter), no derived state. The value is local-only and never transmitted.
 */
let level = 0;
const listeners = new Set<() => void>();

/** Publish a new mic level (0..1). No-op when unchanged so identical frames don't wake the subscriber. */
export function setMicLevel(next: number): void {
  if (next === level) return;
  level = next;
  for (const notify of listeners) notify();
}

/** Current level — the `getSnapshot` for useSyncExternalStore. */
export function getMicLevel(): number {
  return level;
}

/** Subscribe to level changes — the `subscribe` for useSyncExternalStore. Returns an unsubscribe. */
export function subscribeMicLevel(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}
